/**
 * Case studies for the Work page.
 * Only real facts here. Anything in [brackets] is a placeholder the owner
 * still has to supply — don't invent services, outcomes or numbers.
 * Screenshots: drop images in public/work/ and set `screens`.
 */
import type { FixKind } from "../Components/ServiceGraphics/FixGraphic";

export type CaseStudy = {
  id: string;
  client: string;
  /** Services we provided — shown as a route line. */
  services: string[];
  summary: string;
  challenge: string;
  built: string;
  result: string;
  /** Stage visual: device frames for screenshots, or a before/after fix
   *  graphic (FixGraphic preset) for fix work where we don't show the
   *  client's pages. "phones" = two real app screens (PhonePairFrame),
   *  "admin" = AdminPanelGraphic (illustration in our style). */
  frame: "web" | "web-phone" | "phones" | "fix" | "admin";
  /** FixGraphic preset when frame is "fix". */
  fix?: FixKind;
  /** Real screenshots (paths under /public). Placeholders show until set. */
  screens?: { web?: string; phone?: string; phoneBack?: string };
  /** "phones" only: plays a live plate scan over the front screen. Must match the plate in the image. */
  livePlate?: string;
  /** Domain shown in the browser bar (placeholder until confirmed). */
  domain: string;
};

export const CASE_STUDIES: CaseStudy[] = [
  {
    id: "trvlar",
    client: "Trvlar",
    services: ["Website", "iOS app", "Android app"],
    summary: "A long-term partnership across mobile and web development.",
    challenge:
      "Bring trip planning and local discovery into one social product — maps, events, itineraries and friends — on the web, iOS and Android, and keep shipping as it grew.",
    built:
      "A responsive website and React Native apps for iOS and Android: a map with local events, itinerary management, friends and chat.",
    // No metrics until Trvlar shares real ones.
    result:
      "A live website and iOS and Android apps, and a partnership that has run for years — in Trvlar’s words, we kept the team on track and handled challenges with ease.",
    frame: "web-phone",
    // Web: designed from the app screens; replace with a live capture when available.
    screens: { web: "/work/trvlar-web.jpg", phone: "/work/trvlar-app-feed.png" },
    domain: "trvlar.com",
  },
  {
    id: "vera-inkjet",
    client: "Vera InkJet",
    services: ["Website", "Bilingual EN / FR fix"],
    summary: "A fix that keeps Vera InkJet’s bilingual website in the visitor’s language.",
    challenge:
      "Visitors could switch the site to French or English, but the moment they opened another page it snapped back to the default language.",
    built:
      "We made the language choice stick: once a visitor picks EN or FR, every page they open after that stays in that language.",
    result:
      "Visitors read the whole site in the language they chose — and in Vera InkJet’s words: “Excellent service at a fair price. Would recommend.”",
    // Owner chose no screenshot — show the fix as a graphic. Domain unknown.
    frame: "fix",
    fix: "language",
    domain: "",
  },
  {
    id: "nicole-benisti",
    client: "Nicole Benisti",
    services: ["Shopify", "Cart & checkout fix"],
    summary: "A cart fix for a Montreal luxury outerwear house that sells worldwide on Shopify.",
    challenge:
      "Shoppers were hitting a problem in the cart. On a bilingual store selling luxury coats in several currencies, a broken cart means lost orders.",
    built:
      "We found the cause of the cart issue in their Shopify store and fixed it, so customers can add a coat and reach checkout without errors.",
    result: "A cart and checkout that work again on nicolebenisti.com.",
    // Owner chose no screenshot for this client — show the fix as a graphic.
    frame: "fix",
    fix: "cart",
    domain: "nicolebenisti.com",
  },
  {
    id: "jvm-auto",
    client: "JVM Auto",
    services: ["Web admin panel", "Parts management"],
    summary: "A web admin panel for JVM Auto to track work orders and manage parts.",
    challenge:
      "JVM Auto needed one place to see every job in the shop and the parts behind it — where each work order stands, and what’s in stock.",
    built:
      "A web admin panel with work-order tracking from booking to pickup, and a parts management module for the shop’s inventory.",
    result:
      "Every work order and part in one admin panel — each job’s status and the stock behind it, at a glance.",
    // No screenshots yet — illustrated in our style (AdminPanelGraphic).
    frame: "admin",
    domain: "",
  },
  {
    id: "vrt-solutions",
    client: "VRT Solutions",
    services: ["iOS app", "Android app"],
    summary: "A garage-management app for iOS and Android — from scanning a vehicle to sending the invoice.",
    challenge:
      "VRT Solutions needed one mobile app to run a garage job end to end — identify the vehicle, open the work order, inspect it and bill the customer — for the front desk and technicians alike.",
    built:
      "iOS and Android apps with licence-plate and VIN scanning, work orders, vehicle inspections, invoicing and a dedicated technician module.",
    result:
      "One app for the whole job: scan the car, open a work order, inspect, send the invoice — with technicians working from the same app.",
    // Real app screens; the camera view on the scan screen is a mock-up
    // (generic car and plate) and the customer name is replaced.
    frame: "phones",
    screens: { phone: "/work/vrt-app-scan.jpg", phoneBack: "/work/vrt-app-dashboard.jpg" },
    livePlate: "CXRT 482",
    domain: "",
  },
];
